import axios from 'axios';
import Layout from '../shared/Layout';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import usePersistedState from '../../hooks/usePersistedState';
import { useParams, useNavigate } from 'react-router-dom';
import FormStudent from './FormStudent';
import Events from './Events';
import PlannedEvents from './PlannedEvents';
import StudentWords from './StudentWords';
import StudentPhrases from './StudentPhrases';
import StudentCourses from './StudentCourses';
import StudentPlatform from './StudentPlatform';
import PlatformAccessButton from './PlatformAccessButton';
import ChatComponent from '../../components/websocket/ChatComponent';
import LessonDetailModal from './LessonDetailModal';
import RescheduleResponseModal from './RescheduleResponseModal';
import * as rescheduleApi from './rescheduleApi';
import wsManager from '../../services/wsManager';
import IconChat from '../../components/icons/IconChat';
import IconGoBack from '../../components/icons/IconGoBack';
import IconGoogleMeet from '../../components/icons/IconGoogleMeet';
import IconGoogleDocs from '../../components/icons/IconGoogleDocs';
import IconGoogleCalendar from '../../components/icons/IconGoogleCalendar';
import '../../css/student-card.css';

const BASE_TABS = [
   {name: 'profile', label: 'Profile'},
   {name: 'lessons', label: 'Lessons'},
   {name: 'planned', label: 'Planned'},
   {name: 'words', label: 'Words'},
   {name: 'phrases', label: 'Phrases'},
   {name: 'courses', label: 'Courses'},
];

// gdocument/gDictionaryUa/gDictionaryEng all point at Google Docs, so the icon
// alone can't tell them apart - each one that needs it carries a short corner
// badge in addition to the service icon (per user's explicit request).
const RESOURCE_LINK_FIELDS = [
   {name: 'gMeetId', Icon: IconGoogleMeet},
   {name: 'gdocument', Icon: IconGoogleDocs},
   {name: 'gDictionaryUa', Icon: IconGoogleDocs, badge: 'UA'},
   {name: 'gDictionaryEng', Icon: IconGoogleDocs, badge: 'EN'},
];

// "2h 30min" / "3 d 2h 5min" style countdown to a future DateTime.
const formatCountdown = (targetDt, lang) => {
   const totalMinutes = Math.max(0, Math.round(targetDt.diff(DateTime.now(), 'minutes').minutes));
   const days = Math.floor(totalMinutes / 1440);
   const hours = Math.floor((totalMinutes % 1440) / 60);
   const minutes = totalMinutes % 60;
   const parts = [];
   if(days > 0) parts.push(days + ' ' + lang.daysShort);
   if(days > 0 || hours > 0) parts.push(hours + ' ' + lang.hoursShort);
   parts.push(minutes + ' ' + lang.minutesShort);
   return parts.join(' ');
}

// same shape as formatCountdown but the mirror direction - "ago" for a past DateTime.
const formatElapsed = (pastDt, lang) => {
   const totalMinutes = Math.max(0, Math.round(DateTime.now().diff(pastDt, 'minutes').minutes));
   const days = Math.floor(totalMinutes / 1440);
   const hours = Math.floor((totalMinutes % 1440) / 60);
   const minutes = totalMinutes % 60;
   const parts = [];
   if(days > 0) parts.push(days + ' ' + lang.daysShort);
   if(days > 0 || hours > 0) parts.push(hours + ' ' + lang.hoursShort);
   parts.push(minutes + ' ' + lang.minutesShort);
   return parts.join(' ');
}

const StudentCard = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const url = '/api/students/' + id;
   const [data, setData] = useState({});
   const [refresh, setRefresh] = useState(0);
   const [activeTab, setActiveTab] = usePersistedState('student-card-tab', 'profile');
   const [chat, setChat] = useState(false);
   const [savingActive, setSavingActive] = useState(false);
   const [nextLesson, setNextLesson] = useState(null);
   const [nextLessonModalOpen, setNextLessonModalOpen] = useState(false);
   const [lastLesson, setLastLesson] = useState(null);
   const [lastLessonModalOpen, setLastLessonModalOpen] = useState(false);
   const [, setTick] = useState(0);
   const [platformUser, setPlatformUser] = useState(null);
   const [platformRefresh, setPlatformRefresh] = useState(0);
   const [negotiationItems, setNegotiationItems] = useState([]);
   const [negotiationPickerOpen, setNegotiationPickerOpen] = useState(false);
   const [negotiationModalItem, setNegotiationModalItem] = useState(null);
   const [upcomingLessonIds, setUpcomingLessonIds] = useState([]);

   const onRefreshHundler = () => setRefresh(prev => prev + 1);
   const chatTrigger = () => setChat(prev => !prev);
   const onPlatformChanged = () => setPlatformRefresh(prev => prev + 1);

   // keeps the "next lesson in Xh Ymin" countdown and "last lesson Xh Ymin ago" fresh while the page stays open
   useEffect(() => {
      const interval = setInterval(() => setTick(t => t + 1), 60000);
      return () => clearInterval(interval);
   }, []);

   useEffect(() => {
      if(!id) {
         return;
      }
      const today = DateTime.now().toFormat('yyyy-MM-dd');
      axios.get('/api/events?student=' + id + '&dateFrom=' + today, { withCredentials: true })
         .then(response => {
            const events = Array.isArray(response.data) ? response.data : [];
            const now = DateTime.now();
            let closest = null;
            let closestDt = null;
            events.forEach(ev => {
               if(!ev.date || !ev.start) {
                  return;
               }
               const dt = DateTime.fromFormat(ev.date + ' ' + ev.start, 'yyyy-MM-dd HH:mm');
               if(!dt.isValid || dt <= now) {
                  return;
               }
               if(!closestDt || dt < closestDt) {
                  closestDt = dt;
                  closest = ev;
               }
            });
            setNextLesson(closest);
            // Reused for the WS subscription set below, not just the
            // countdown pill - every upcoming lesson is a plausible target
            // for a live reschedule/cancel event (the 4h gate rules out
            // anything closer, so this is a correctly-bounded set, not
            // "subscribe to everything").
            setUpcomingLessonIds([...new Set(events.map(ev => ev.lesson && ev.lesson.id).filter(Boolean))]);
         })
         .catch(() => setNextLesson(null));
   }, [id, refresh]);

   // Keeps the header negotiation badge live (appear on a new request,
   // disappear once resolved) instead of only refreshing on the teacher's
   // own in-app actions - mirrors RescheduleResponseModal.jsx/
   // LessonDetailModal.jsx's subscription pattern, just fanned out over
   // every upcoming lesson since this view isn't scoped to just one.
   useEffect(() => {
      if(!upcomingLessonIds.length) return;
      const handler = () => loadNegotiationItems();
      upcomingLessonIds.forEach(lessonId => wsManager.subscribe('/topic/reschedule/' + lessonId, handler));
      return () => upcomingLessonIds.forEach(lessonId => wsManager.unsubscribe('/topic/reschedule/' + lessonId, handler));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [upcomingLessonIds]);

   useEffect(() => {
      if(!id) {
         return;
      }
      const today = DateTime.now().toFormat('yyyy-MM-dd');
      axios.get('/api/events?student=' + id + '&dateTo=' + today, { withCredentials: true })
         .then(response => {
            const events = Array.isArray(response.data) ? response.data : [];
            const now = DateTime.now();
            let closest = null;
            let closestDt = null;
            events.forEach(ev => {
               if(!ev.date || !ev.start) {
                  return;
               }
               const dt = DateTime.fromFormat(ev.date + ' ' + ev.start, 'yyyy-MM-dd HH:mm');
               if(!dt.isValid || dt > now) {
                  return;
               }
               if(!closestDt || dt > closestDt) {
                  closestDt = dt;
                  closest = ev;
               }
            });
            setLastLesson(closest);
         })
         .catch(() => setLastLesson(null));
   }, [id, refresh]);

   // Drives both the header's grant-access/status slot and whether the
   // "Платформа" tab exists at all - null means no UserPlatform account
   // was ever created for this student (studentId enforces at most one on
   // the backend, so this is always 0 or 1 items).
   useEffect(() => {
      if(!id) {
         return;
      }
      axios.get('/api/platform-users?studentId=' + id, { withCredentials: true })
         .then(response => {
            const list = Array.isArray(response.data) ? response.data : [];
            setPlatformUser(list.length ? list[0] : null);
         })
         .catch(() => setPlatformUser(null));
   }, [id, platformRefresh]);

   // Header-level "something needs your response" indicator - reschedule
   // and cancel requests from the platform. Kept live via the WS
   // subscription below (one topic per upcoming lesson), this fetch just
   // covers the initial load and any teacher-triggered refresh.
   const loadNegotiationItems = () => {
      if(!id) return;
      Promise.all([
         rescheduleApi.getPendingRescheduleRequests(id).catch(() => ({ data: [] })),
         rescheduleApi.getPendingCancelRequests(id).catch(() => ({ data: [] })),
      ]).then(([rescheduleRes, cancelRes]) => {
         const reschedules = (Array.isArray(rescheduleRes.data) ? rescheduleRes.data : []).map(r => ({ type: 'reschedule', id: r.id, raw: r }));
         const cancels = (Array.isArray(cancelRes.data) ? cancelRes.data : []).map(r => ({ type: 'cancel', id: r.id, raw: r }));
         setNegotiationItems([...reschedules, ...cancels]);
      });
   };
   useEffect(loadNegotiationItems, [id, refresh, platformRefresh]);

   // Same "does the Платформа tab have anything the teacher needs to look
   // at" signal, but for the OTHER pending-approval family shown in that
   // same tab's "Очікують підтвердження" section (TeacherApprovalRequest -
   // blur-reveal etc, unrelated to reschedule/cancel) - StudentPlatform.jsx
   // fetches its own copy once the tab is actually open, this is a second,
   // independent fetch purely so the tab LABEL itself can show a dot before
   // it's ever clicked. No WS for this one (TeacherApprovalRequest has no
   // per-student broadcast to subscribe to), refreshes on the same
   // mount/refresh cadence as everything else here.
   const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
   useEffect(() => {
      if(!id || !platformUser) { setPendingApprovalCount(0); return; }
      axios.get('/api/teacher-approval-requests', { params: { student: id }, withCredentials: true })
         .then(response => setPendingApprovalCount(Array.isArray(response.data) ? response.data.length : 0))
         .catch(() => setPendingApprovalCount(0));
   }, [id, refresh, platformRefresh, platformUser]);

   const hasPendingPlatformApproval = negotiationItems.length > 0 || pendingApprovalCount > 0;

   const handleNegotiationBadgeClick = () => {
      if(negotiationItems.length === 1) {
         setNegotiationModalItem(negotiationItems[0]);
      } else {
         setNegotiationPickerOpen(prev => !prev);
      }
   };
   const handleNegotiationItemPick = (item) => {
      setNegotiationPickerOpen(false);
      setNegotiationModalItem(item);
   };
   const handleNegotiationResolved = () => {
      loadNegotiationItems();
      onRefreshHundler();
   };

   const handleToggleActive = () => {
      if(savingActive || !data.id) {
         return;
      }
      // /api/booleans convention used everywhere else in this app: 1=Yes, 2=No.
      const newValue = data.isActive === 1 ? 2 : 1;
      setSavingActive(true);
      axios.put(url, JSON.stringify({ isActive: String(newValue) }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setData(prev => ({...prev, isActive: newValue}));
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
         }
         console.log(err);
      }).finally(() => setSavingActive(false));
   }

   useEffect(() => {
      axios.get(url, { withCredentials: true })
         .then(response => setData(response.data))
         .catch(err => {
            if(err.response && err.response.status === 401) navigate('/login');
         });
   }, [url, refresh, navigate]);

   const lang = language[language.lang];
   const initials = ((data.name || '?').charAt(0) + (data.surname || '').charAt(0)).toUpperCase();
   // some legacy student columns store the literal string "null" instead of a real NULL
   const resourceLinks = RESOURCE_LINK_FIELDS.filter(f => data[f.name] && data[f.name] !== 'null');
   // "Платформа" only exists once the student actually has a UserPlatform
   // account (pending or active) - per explicit user scope, it appears the
   // moment access is granted, not only once activated.
   const tabs = platformUser ? [...BASE_TABS, {name: 'platform', label: 'Platform'}] : BASE_TABS;

   return (
      <Layout>
         <main className='main-block'>
            <div className='main-content student-card-page'>

               {chat ? (
                  <ChatComponent studentId={id} closeCallback={() => setChat(false)} />
               ) : ''}

               <div className='student-card-header'>
                  <div className='student-card-back' onClick={() => navigate('/students')} title={lang.students}>
                     <IconGoBack style={{fill:'var(--lighttext-color)', width:18, height:18, margin:0}} />
                  </div>
                  <div className='student-card-avatar'>{initials}</div>
                  <div className='student-card-title-block'>
                     <div className='student-card-name-row'>
                        <h2 className='student-card-name'>{data.name} {data.surname}</h2>
                        {resourceLinks.length ? (
                           <div className='student-card-resource-links'>
                              {resourceLinks.map(f => (
                                 <a key={f.name} className='student-card-resource-link'
                                    href={data[f.name]} target='_blank' rel='noopener noreferrer'
                                    title={lang[f.name]} onClick={e => e.stopPropagation()}>
                                    <f.Icon style={{width:15, height:15}} />
                                    {f.badge ? <span className='student-card-resource-badge'>{f.badge}</span> : ''}
                                 </a>
                              ))}
                           </div>
                        ) : ''}
                     </div>
                     <div className='student-card-meta-row'>
                        <span
                           className={'student-card-status-pill toggle-clickable' + (data.isActive === 1 ? ' on' : '') + (savingActive ? ' toggle-pending' : '')}
                           onClick={handleToggleActive}
                           title={lang.isActive}>
                           <i className='student-card-status-dot'></i>
                           {data.isActive === 1 ? lang.active : lang.inactive}
                        </span>
                        {negotiationItems.length ? (
                           <span className='student-card-negotiation-badge' onClick={handleNegotiationBadgeClick}
                              title='Учень надіслав запит на зміну терміну чи скасування заняття'>
                              {negotiationItems.length === 1
                                 ? (negotiationItems[0].type === 'reschedule' ? 'Запит на зміну терміну' : 'Запит на скасування')
                                 : negotiationItems.length + ' запити очікують відповіді'}
                           </span>
                        ) : ''}
                        {negotiationPickerOpen ? (
                           <div className='student-card-negotiation-picker'>
                              {negotiationItems.map(item => (
                                 <div key={item.type + '-' + item.id} className='student-card-negotiation-picker-item'
                                    onClick={() => handleNegotiationItemPick(item)}>
                                    {item.type === 'reschedule' ? 'Зміна терміну' : 'Скасування'} - {item.type === 'reschedule' ? (item.raw.lesson && item.raw.lesson.date) : (item.raw.event && item.raw.event.lesson && item.raw.event.lesson.date)}
                                 </div>
                              ))}
                           </div>
                        ) : ''}
                        {data.telegramUsername ? <span className='student-card-telegram'>@{data.telegramUsername}</span> : ''}
                        {lastLesson ? (
                           <span className='student-card-next-lesson-pill toggle-clickable last'
                              onClick={() => setLastLessonModalOpen(true)}
                              title={lang.modals.lessons.updateTitle}>
                              <i className='student-card-next-lesson-dot'></i>
                              {lang.lastLesson} {formatElapsed(DateTime.fromFormat(lastLesson.date + ' ' + lastLesson.start, 'yyyy-MM-dd HH:mm'), lang)} {lang.agoSuffix}
                              <span className='student-card-next-lesson-date'>
                                 {DateTime.fromFormat(lastLesson.date + ' ' + lastLesson.start, 'yyyy-MM-dd HH:mm').toFormat('dd.MM.yyyy, HH:mm')}
                              </span>
                              {lastLesson.lesson && lastLesson.lesson.googleEventHtmlLink ? (
                                 <a className='student-card-next-lesson-calendar-link'
                                    href={lastLesson.lesson.googleEventHtmlLink} target='_blank' rel='noopener noreferrer'
                                    onClick={e => e.stopPropagation()} title='Google Calendar'>
                                    <IconGoogleCalendar fillColor='icon-red' style={{width:14, height:14}} />
                                 </a>
                              ) : ''}
                           </span>
                        ) : ''}
                        {nextLesson ? (
                           <span className='student-card-next-lesson-pill toggle-clickable next'
                              onClick={() => setNextLessonModalOpen(true)}
                              title={lang.modals.lessons.updateTitle}>
                              <i className='student-card-next-lesson-dot'></i>
                              {lang.nextLesson} {formatCountdown(DateTime.fromFormat(nextLesson.date + ' ' + nextLesson.start, 'yyyy-MM-dd HH:mm'), lang)}
                              <span className='student-card-next-lesson-date'>
                                 {DateTime.fromFormat(nextLesson.date + ' ' + nextLesson.start, 'yyyy-MM-dd HH:mm').toFormat('dd.MM.yyyy, HH:mm')}
                              </span>
                              {nextLesson.lesson && nextLesson.lesson.googleEventHtmlLink ? (
                                 <a className='student-card-next-lesson-calendar-link'
                                    href={nextLesson.lesson.googleEventHtmlLink} target='_blank' rel='noopener noreferrer'
                                    onClick={e => e.stopPropagation()} title='Google Calendar'>
                                    <IconGoogleCalendar fillColor='icon-red' style={{width:14, height:14}} />
                                 </a>
                              ) : ''}
                           </span>
                        ) : ''}
                     </div>
                  </div>
                  {data.addedOuterUser ? (
                     <div onClick={chatTrigger} className='student-card-chat-btn' title={lang.messages}>
                        <IconChat fillColor='icon-green' />
                     </div>
                  ) : ''}
                  <PlatformAccessButton studentId={id} studentName={(data.name || '') + ' ' + (data.surname || '')}
                     studentEmail={data.email && data.email !== 'null' ? data.email : ''}
                     platformUser={platformUser} onChanged={onPlatformChanged} />
               </div>

               <div className='student-card-tabs'>
                  {tabs.map(tab => (
                     <div key={tab.name}
                        className={'student-card-tab' + (activeTab === tab.name ? ' active' : '')}
                        onClick={() => setActiveTab(tab.name)}>
                        {lang[tab.label]}
                        {tab.name === 'platform' && hasPendingPlatformApproval ? <i className='student-card-tab-dot'></i> : ''}
                     </div>
                  ))}
               </div>

               {activeTab === 'profile' ? <FormStudent data={data} url={url} onRefreshHundler={onRefreshHundler} /> : ''}
               {activeTab === 'lessons' ? <Events /> : ''}
               {activeTab === 'planned' ? <PlannedEvents /> : ''}
               {activeTab === 'words' ? <StudentWords /> : ''}
               {activeTab === 'phrases' ? <StudentPhrases /> : ''}
               {activeTab === 'courses' ? <StudentCourses /> : ''}
               {activeTab === 'platform' && platformUser ? <StudentPlatform /> : ''}

               <LessonDetailModal isOpen={nextLessonModalOpen} lessonId={nextLesson && nextLesson.lesson && nextLesson.lesson.id}
                  onClose={() => setNextLessonModalOpen(false)}
                  onSaved={() => { setNextLessonModalOpen(false); onRefreshHundler(); }}
                  onParticipantsChanged={onRefreshHundler} />
               <LessonDetailModal isOpen={lastLessonModalOpen} lessonId={lastLesson && lastLesson.lesson && lastLesson.lesson.id}
                  onClose={() => setLastLessonModalOpen(false)}
                  onSaved={() => { setLastLessonModalOpen(false); onRefreshHundler(); }}
                  onParticipantsChanged={onRefreshHundler} />
               <RescheduleResponseModal key={negotiationModalItem ? negotiationModalItem.type + '-' + negotiationModalItem.id : 'none'}
                  isOpen={!!negotiationModalItem} item={negotiationModalItem}
                  onClose={() => setNegotiationModalItem(null)}
                  onResolved={handleNegotiationResolved} />
            </div>
         </main>
      </Layout>
   );
}

export default StudentCard;
